import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";
import { TYPE_BADGE_COLORS, TYPE_TEXT_COLORS } from "../../utils/typeColors";

interface Props { payload: VisualPayload }

// plan phase 5 (decision 0021): the verdict computed from the score gap, the same words the voice uses.
const VERDICT_STYLE: Record<string, string> = {
  toss_up: "bg-white/10 text-white/70 border-white/15",
  slight:  "bg-sky-500/20 text-sky-300 border-sky-500/30",
  clear:   "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  much:    "bg-emerald-500/30 text-emerald-200 border-emerald-400/40",
};

export default function ComparisonCard({ payload }: Props) {
  const pokemonSections = payload.sections.filter((s) => s.section === "pokemon");
  const verdict = payload.sections.find((s) => s.section === "verdict") as Record<string, unknown> | undefined;
  // plan phase 6 (decision 0024): the simulated one-on-one, or the typing both ways when no simulation covers the pair.
  const matchup = payload.sections.find((s) => s.section === "matchup") as Record<string, unknown> | undefined;
  if (pokemonSections.length === 0) {
    // fallback: show empty message
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
        <div className="text-white/70 text-sm py-6 text-center">
          No comparison data available. Try asking about two or more Pokémon.
        </div>
      </CardShell>
    );
  }
  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      {matchup && (
        <div className="mb-3 rounded-lg border border-white/[0.07] p-3">
          {matchup.simulated ? (
            <p className="text-sm text-white/90">
              <span className="text-[10px] text-white/30 uppercase tracking-wide mr-2">Simulated 1v1</span>
              {String(matchup.winner)} beats {String(matchup.loser)}
              {typeof matchup.winner_rating === "number" && <span className="text-white/50"> · {matchup.winner_rating}/1000</span>}
            </p>
          ) : (
            <p className="text-sm text-white/70"><span className="text-[10px] text-white/30 uppercase tracking-wide mr-2">No simulation</span>typing only for this pair</p>
          )}
          {(() => {
            const t = (matchup.typing ?? {}) as Record<string, { best_type?: string; phrase?: string }>;
            if (!t.a || !t.b) return null;
            return (
              <p className="text-[11px] text-white/45 mt-1">
                {String(matchup.a)}: {t.a.best_type} is {t.a.phrase} · {String(matchup.b)}: {t.b.best_type} is {t.b.phrase}
              </p>
            );
          })()}
        </div>
      )}
      {verdict && typeof verdict.label === "string" && (
        <div className="flex items-center gap-2 mb-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border leading-none ${VERDICT_STYLE[String(verdict.kind)] ?? VERDICT_STYLE.toss_up}`}>{verdict.label}</span>
          {typeof verdict.gap === "number" && <span className="text-[11px] text-white/40">{verdict.gap} points apart</span>}
        </div>
      )}
      <div className="flex flex-col md:flex-row gap-6 justify-center">
        {pokemonSections.map((section: any, idx: number) => (
          <div key={idx} className="flex-1 min-w-[220px] max-w-xs bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg font-bold text-white leading-tight">{section.pokemon_name}</span>
              <span className="text-xs text-white/40">#{section.pokedex_id}</span>
            </div>
            {section.rank != null && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs px-2 py-0.5 rounded-full bg-scout-yellow/10 text-scout-yellow font-medium">
                  Rank #{(section.rank as number).toLocaleString()}
                </span>
                {section.score != null && (
                  <span className="text-xs text-white/40">Score {section.score}</span>
                )}
              </div>
            )}
            {(section.best_fast_move || section.best_charge_move_1) && (
              <div className="mb-2 text-[11px] text-white/50">
                <span className="text-white/30">Best: </span>
                {section.best_fast_move as string}
                {section.best_charge_move_1 && (
                  <span> / {section.best_charge_move_1 as string}{section.best_charge_move_2 ? ` & ${section.best_charge_move_2 as string}` : ""}</span>
                )}
              </div>
            )}
            <div className="flex items-center gap-1.5 mb-2">
              <span className={`text-xs font-medium ${TYPE_TEXT_COLORS[section.type1?.toLowerCase()] ?? "text-gray-300"}`}>{section.type1}</span>
              {section.type2 && (
                <>
                  <span className="text-white/20">·</span>
                  <span className={`text-xs font-medium ${TYPE_TEXT_COLORS[section.type2?.toLowerCase()] ?? "text-gray-300"}`}>{section.type2}</span>
                </>
              )}
            </div>
            <div className="space-y-1 mb-2">
              <div className="flex justify-between text-xs text-white/40">
                <span>ATK</span><span>{section.base_attack}</span>
              </div>
              <div className="flex justify-between text-xs text-white/40">
                <span>DEF</span><span>{section.base_defense}</span>
              </div>
              <div className="flex justify-between text-xs text-white/40">
                <span>STA</span><span>{section.base_stamina}</span>
              </div>
            </div>
            {section.league_capped_cp !== undefined && section.league_capped_cp !== null ? (
              <div className="mt-2 text-xs text-white/30 space-y-1">
                <div>
                  <span>League-capped CP: </span>
                  <span className="text-white font-bold">{section.league_capped_cp}</span>
                  {section.league && (
                    <span className="ml-1 text-white/40">({section.league?.charAt(0).toUpperCase() + section.league?.slice(1)} League)</span>
                  )}
                </div>
                <div>
                  <span>Optimal IVs: </span>
                  <span className="text-white font-bold">
                    {Array.isArray(section.league_capped_iv)
                      ? `${section.league_capped_iv[0]}/${section.league_capped_iv[1]}/${section.league_capped_iv[2]}`
                      : "?/?/?"}
                  </span>
                  <span className="ml-2">Level: <span className="text-white font-bold">{section.league_capped_level?.toFixed(1)}</span></span>
                </div>
              </div>
            ) : (
              <div className="flex justify-between text-xs text-white/30 mt-2">
                <span>L40 CP: <span className="text-white font-bold">{section.max_cp_l40}</span></span>
                <span>L50 CP: <span className="text-white font-bold">{section.max_cp_l50}</span></span>
              </div>
            )}

            {/* Movesets */}
            {((section.fast_moves?.length > 0) || (section.charged_moves?.length > 0)) && (
              <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
                {section.fast_moves?.length > 0 && (
                  <div>
                    <span className="text-[10px] uppercase tracking-wide text-white/30 block mb-1">Fast</span>
                    <div className="flex flex-wrap gap-1">
                      {section.fast_moves.map((m: { name: string; type?: string }) => (
                        <span
                          key={m.name}
                          className={`text-[11px] rounded px-1.5 py-0.5 font-medium ${
                            m.type ? (TYPE_BADGE_COLORS[m.type.toLowerCase()] ?? "bg-white/[0.07] text-white/70") : "bg-white/[0.07] text-white/70"
                          }`}
                        >{m.name}</span>
                      ))}
                    </div>
                  </div>
                )}
                {section.charged_moves?.length > 0 && (
                  <div>
                    <span className="text-[10px] uppercase tracking-wide text-white/30 block mb-1">Charged</span>
                    <div className="flex flex-wrap gap-1">
                      {section.charged_moves.map((m: { name: string; type?: string }) => (
                        <span
                          key={m.name}
                          className={`text-[11px] rounded px-1.5 py-0.5 font-medium ${
                            m.type ? (TYPE_BADGE_COLORS[m.type.toLowerCase()] ?? "bg-white/[0.07] text-white/70") : "bg-white/[0.07] text-white/70"
                          }`}
                        >{m.name}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </CardShell>
  );
}
